import React from 'react';
import styled from 'styled-components';
import Button from 'components/Button';
import { Spin } from 'antd';
import request from 'config/request';
import endpoints from 'config/endpoints';
import showNotification from 'utils/notification';
import { getMicrositeSubscibrKey } from 'constant/constants';
import * as ga from 'lib/ga';
import { TRACKING } from 'constant/analytics';

interface UnsubscribeModalProps {
  theme: Record<any, any>;
  loading: boolean;
  subscribeData: Record<any, any>;
  logo: string;
  slug: string;
  onClose: any;
  setIsSessionSubscribed: any;
  email: any;
  setUnsubscribed: any;
}
interface UnsubscribeModalWrapProps {
  $fontType: string;
  $hightlightColor: string;
  $fontColor: string;
}

interface SubscribeButtonWrapperProps {
  $fontFamily: string;
  $backgroundColor: string;
  $borderColor?: string;
}

const UnsubscribeModal = ({
  theme,
  loading,
  subscribeData,
  logo,
  slug,
  onClose,
  setIsSessionSubscribed,
  email,
  setUnsubscribed,
}: UnsubscribeModalProps) => {
  const {
    subscribeModalFontType,
    subscribeModalFontColor,
    subscribeModalHighlightColor,
    subscribeModalFooterButtonBackgroundColor,
    subscribeModalFooterButtonFontType,
  } = theme;

  if (loading) {
    return (
      <MicrositeLoader>
        <Spin />
      </MicrositeLoader>
    );
  }

  const handleSubmit = async () => {
    ga.event(TRACKING.HOME.UNSUBSCRIBE.UNSUBSCRIBE_NOW);
    if (email && slug) {
      const requestUrl = await `${endpoints.momos.microsite.unsubscribe}`.replace(/:slug/g, slug);
      const requestPayload = { email };
      const response = await request.post(requestUrl, requestPayload);
      if (response?.data?.status) {
        const successMsg = `You have unsubscribed`;
        showNotification(successMsg, 'Unsubscribed', 'success');
        const brandMicrositeKey = getMicrositeSubscibrKey(slug);
        localStorage.removeItem(brandMicrositeKey);
        onClose();
        ga.event(TRACKING.HOME.UNSUBSCRIBE.MODAL_CLOSE);
        setIsSessionSubscribed(false);
        setUnsubscribed(false);
      }
    }
  };

  return (
    <UnsubscribeModalWrap
      $fontColor={subscribeModalFontColor}
      $fontType={subscribeModalFontType}
      $hightlightColor={subscribeModalHighlightColor}
    >
      <div className="hero-container">
        <div className="modal-container">
          <div className="modal-bottom-container">
            {logo && subscribeData?.hideLogo && <img src={logo} className="microsite-desk-logo" />}
            <div className="bottom-heading-container">
              <p className="heading-text mb-0" style={{ color: subscribeModalFontColor }}>
                {subscribeData?.unSubscribeModalTitle || 'Unsubscribe?'}
              </p>
              <p className="body-text mb-0">{subscribeData?.unSubscribeModalDescription || ''}</p>
            </div>
            <div className="button-container">
              <SubscribeButtonWrapper
                $backgroundColor={subscribeModalFooterButtonBackgroundColor}
                $fontFamily={subscribeModalFooterButtonFontType}
                $borderColor={`grey`}
              >
                <Button
                  key="save"
                  type="primary"
                  onClick={handleSubmit}
                  style={{
                    backgroundColor: subscribeModalFooterButtonBackgroundColor,
                    borderColor: subscribeModalFooterButtonBackgroundColor,
                    fontFamily: subscribeModalFooterButtonFontType,
                  }}
                >
                  {subscribeData?.unSubscribeModalFooterButtonTitle || 'Yes, unsubscribe'}
                </Button>
              </SubscribeButtonWrapper>
              <SubscribeButtonWrapper
                $backgroundColor={subscribeModalFooterButtonBackgroundColor}
                $fontFamily={subscribeModalFooterButtonFontType}
                $borderColor={`grey`}
                color={`#000000`}
              >
                <Button
                  key="save"
                  type="primary"
                  onClick={() => {
                    ga.event(TRACKING.HOME.UNSUBSCRIBE.CANCEL);
                    onClose();
                  }}
                  style={{
                    backgroundColor: `var(--white)`,
                    fontFamily: subscribeModalFooterButtonFontType,
                    color: subscribeModalFooterButtonBackgroundColor,
                    borderColor: `grey`,
                  }}
                >
                  {'Cancel'}
                </Button>
              </SubscribeButtonWrapper>
            </div>
          </div>
        </div>
      </div>
    </UnsubscribeModalWrap>
  );
};

export default UnsubscribeModal;

const UnsubscribeModalWrap = styled.div<UnsubscribeModalWrapProps>`
  height: 100%;
  overflow: hidden;
  overflow-y: auto;

  ::-webkit-scrollbar {
    width: 4px;
    height: 8px;
    background-color: transparent;
  }

  ::-webkit-scrollbar-thumb {
    background: var(--grey-light);
  }

  ::selector {
    background: ${(props) => props.$hightlightColor};
  }

  .hero-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    .modal-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      border-radius: 10px;
      width: 100%;
      background: var(--white);

    }

    .modal-top-container {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      align-items: flex-start;
      padding: 30px 10px 20px 10px;
      gap: 0px;
      width: 100%;
    }

    .modal-bottom-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      background: var(--white);
      width: 100%;
      border-radius: 10px;
      gap : 20px;

      .microsite-desk-logo {
        display: block;
        margin: 0 auto;
        width: 240px;
        height: 96px;
        object-fit: contain;
        margin-bottom: 10px;
      }

      .bottom-heading-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 10px;

        .heading-text {
          font-family: ${(props) => props.$fontType};
          color: ${(props) => props.$fontColor};
          font-style: normal;
          font-size: 24px;
          display: flex;
          align-items: center;
          text-align: center;
          margin-bottom: 5px;
        }

        .body-text {
          font-family: ${(props) => props.$fontType};
          color: ${(props) => props.$fontColor};
          font-style: normal;
          font-size: 16px;
          text-align: center;
          margin-bottom: 5px;
        }
      }

      
    .button-container {
        width: 100%;
      }
  }
`;

const MicrositeLoader = styled.div`
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const SubscribeButtonWrapper = styled.div<SubscribeButtonWrapperProps>`
  width: 100%;
  margin-top: 20px;

  .ant-btn-primary {
    font-family: ${(props) => props.$fontFamily};
    background: ${(props) => props.$backgroundColor};
    border-color: ${(props) => (props.$borderColor ? props.$borderColor : props.$backgroundColor)};
    width: 100%;
    border-radius: 25px;
    &:hover {
      filter: brightness(90%);
    }
  }

  .ant-btn span {
    font-family: ${(props) => props.$fontFamily};
    color: ${(props) => (props.color ? props.color : 'var(--white)')};
    font-size: 16px;
    line-height: 20px;
  }
`;
